/**
 * Getting a deck out of the app.
 *
 * Four targets, because church AV desks do not agree on one: PowerPoint for the
 * volunteer who wants to restyle it, PNGs for ProPresenter and EasyWorship,
 * PDF for anything that can open a PDF, and Marp markdown for anyone who wants
 * the source. Every one of them reads `parseSlides` output and nothing else.
 *
 * All four take the same `SlideStyle` — theme and frame shape — so a deck set
 * up for a 4:3 projector comes out 4:3 in every format, and the preview in the
 * editor is what lands on the screen.
 *
 * `pptxgenjs` and `jszip` are imported dynamically. Together they are larger
 * than the rest of the editor, and someone writing a sermon should not download
 * them to do it.
 */

import type { Slide } from "@/lib/slides";
import { toMarpMarkdown } from "@/lib/slides";
import { MIN_SLIDE_SCALE, plainText, slideToPng } from "@/lib/slide-render";
import { printHtml } from "@/lib/print";
import {
  DEFAULT_SLIDE_STYLE,
  PPTX_FACES,
  PX_PER_INCH,
  SLIDE_FACES,
  SLIDE_GEOMETRY,
  type SlideStyle,
} from "@/lib/slide-themes";

/** Used when a caller has no style to hand — see the note in slide-render. */
const FALLBACK = DEFAULT_SLIDE_STYLE;

/** A filename stem safe on every platform, derived from the document title. */
export function safeFileName(title: string): string {
  const cleaned = title
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return cleaned || "sermon";
}

/**
 * Save an export using the platform's own file workflow.
 *
 * A Mac app should never drop an important sermon into an invisible webview
 * downloads folder. Tauri gets a Finder save panel and an explicit write;
 * the fallback website keeps the familiar browser download.
 */
export async function downloadBlob(blob: Blob, filename: string): Promise<void> {
  if ("__TAURI_INTERNALS__" in window) {
    const [{ save }, { writeFile }] = await Promise.all([
      import("@tauri-apps/plugin-dialog"),
      import("@tauri-apps/plugin-fs"),
    ]);
    const path = await save({ defaultPath: filename });
    if (!path) return;
    await writeFile(path, new Uint8Array(await blob.arrayBuffer()));
    return;
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Revoking synchronously can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Slide pixels to PowerPoint inches. */
const inches = (px: number) => px / PX_PER_INCH;
/** Slide pixels to points, PowerPoint's unit for type. */
const points = (px: number) => (px * 72) / PX_PER_INCH;

const hex = (colour: string) => colour.replace("#", "");

/**
 * Export as PowerPoint.
 *
 * Real text boxes, not pictures of text: the whole reason someone asks for
 * PPTX rather than PNGs is to change something once it is open. Positions come
 * from the same constants the PNG renderer draws with, so the two look alike.
 */
export async function exportPptx(
  slides: Slide[],
  title: string,
  style: SlideStyle = FALLBACK,
): Promise<void> {
  const { default: PptxGenJS } = await import("pptxgenjs");
  const { margin, titleSize, titleOnlySize, bodySize, quoteSize } = SLIDE_GEOMETRY;
  const { theme, aspect } = style;
  const { ink, soft, accent, background, ornament } = theme;
  const titleFace = PPTX_FACES[theme.titleFace];
  const quoteFace = PPTX_FACES[theme.quoteFace];

  const deck = new PptxGenJS();
  deck.layout = aspect.pptxLayout;
  deck.title = title;

  const pad = inches(margin);
  const contentWidth = inches(aspect.width - margin * 2);
  // Less the footer strip, so a full slide never overlaps the rule below it —
  // and, as on the canvas, a theme drawing no ornament gets that space back.
  const footer = ornament ? SLIDE_GEOMETRY.footer : 0;
  const contentHeight = inches(aspect.height - margin * 2 - footer);

  for (const slide of slides) {
    const page = deck.addSlide();
    // PowerPoint defaults to a white slide, so a dark theme has to say so.
    page.background = { color: hex(background) };

    /** Anything we could not paint gets named in the notes — see slide-render. */
    const images = slide.blocks.filter((block) => block.kind === "image");

    if (slide.layout === "title" || slide.layout === "quote") {
      const isQuote = slide.layout === "quote";
      const text = slide.blocks
        .map((block) => {
          if (block.kind === "heading") return plainText(block.text);
          if (block.kind === "quote") return `“${plainText(block.text)}”`;
          return "text" in block ? plainText(block.text) : "";
        })
        .filter(Boolean)
        .join("\n");

      page.addText(text, {
        x: pad,
        y: pad,
        w: contentWidth,
        h: contentHeight,
        align: "center",
        valign: "middle",
        fontSize: points(isQuote ? quoteSize : titleOnlySize),
        fontFace: isQuote ? quoteFace : titleFace,
        italic: isQuote,
        bold: !isQuote,
        color: hex(ink),
        shrinkText: true,
      });
    } else {
      const heading = slide.blocks.find((block) => block.kind === "heading");
      let cursor = pad;

      if (heading && heading.kind === "heading") {
        const headingHeight = inches(titleSize * 1.6);
        page.addText(plainText(heading.text), {
          x: pad,
          y: cursor,
          w: contentWidth,
          h: headingHeight,
          fontSize: points(titleSize),
          fontFace: titleFace,
          bold: true,
          color: hex(ink),
          valign: "top",
          // A long title wraps to two lines and would otherwise push its box
          // down over the body copy.
          shrinkText: true,
        });
        cursor += headingHeight;
      }

      const body = slide.blocks
        .filter((block) => block !== heading && block.kind !== "image")
        .map((block) => {
          if (block.kind === "bullet") {
            return {
              text: plainText(block.text),
              options: { bullet: true, indentLevel: block.depth, color: hex(ink) },
            };
          }
          if (block.kind === "quote") {
            return { text: `“${plainText(block.text)}”`, options: { italic: true, color: hex(soft) } };
          }
          return { text: "text" in block ? plainText(block.text) : "", options: { color: hex(ink) } };
        })
        .filter((run) => run.text);

      if (body.length) {
        page.addText(body, {
          x: pad,
          y: cursor,
          w: contentWidth,
          h: contentHeight - (cursor - pad),
          fontSize: points(bodySize),
          fontFace: titleFace,
          valign: "top",
          // PowerPoint shrinks the type itself rather than overflowing the box,
          // which matches what the PNG renderer does.
          shrinkText: true,
        });
      }
    }

    // Alt text for anything we could not fetch — see the note in slide-render.
    // Outside the layout branch, so a title card carrying a logo says so too.
    if (images.length) {
      page.addNotes(
        images.map((block) => (block.kind === "image" ? `Image: ${block.url}` : "")).join("\n"),
      );
    }

    // The same footer the PNG and PDF paths draw, so a deck exported three ways
    // is recognisably one deck — and omitted entirely by a theme that wants
    // nothing on the page but the words.
    if (!ornament) continue;

    page.addShape("rect", {
      x: pad,
      y: inches(aspect.height - margin + 20),
      w: inches(120),
      h: inches(9),
      fill: { color: hex(accent) },
      line: { color: hex(accent) },
    });

    if (slides.length > 1) {
      page.addText(`${slide.index + 1} / ${slides.length}`, {
        x: inches(aspect.width - margin - 300),
        y: inches(aspect.height - margin - 10),
        w: inches(300),
        h: inches(50),
        align: "right",
        valign: "middle",
        fontSize: points(32),
        fontFace: titleFace,
        color: hex(soft),
      });
    }
  }

  const blob = (await deck.write({ outputType: "blob" })) as Blob;
  await downloadBlob(blob, `${safeFileName(title)}-slides.pptx`);
}

/**
 * Export as a zip of PNGs, one per slide.
 *
 * The format presentation software actually imports without argument:
 * ProPresenter and EasyWorship both take a folder of images and make a
 * playlist of it. Numbered from 01 so they sort correctly in every file
 * browser.
 */
export async function exportImageZip(
  slides: Slide[],
  title: string,
  style: SlideStyle = FALLBACK,
): Promise<void> {
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  const stem = safeFileName(title);
  const width = String(slides.length).length;

  for (const slide of slides) {
    const png = await slideToPng(slide, { ...style, total: slides.length });
    const number = String(slide.index + 1).padStart(Math.max(2, width), "0");
    zip.file(`${stem}-${number}.png`, png);
  }

  // The outline doubles as a contact sheet for whoever operates the desk.
  zip.file(
    "slides.txt",
    slides.map((slide) => `${slide.index + 1}. ${slide.title}`).join("\n") + "\n",
  );

  const blob = await zip.generateAsync({ type: "blob" });
  await downloadBlob(blob, `${stem}-slides.zip`);
}

/** Export the deck as Marp markdown. */
export async function exportMarp(
  slides: Slide[],
  title: string,
  style: SlideStyle = FALLBACK,
): Promise<void> {
  const blob = new Blob([toMarpMarkdown(slides, title, style.aspect.marpSize)], {
    type: "text/markdown;charset=utf-8",
  });
  await downloadBlob(blob, `${safeFileName(title)}-slides.md`);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * An image URL the print window is allowed to load.
 *
 * The print sheet is written into a fresh window with `document.write`, so a
 * `javascript:` or `data:text/html` URL in a document would run there. Anything
 * that is not plainly a picture is dropped and the alt text stands in, which is
 * what the canvas path does for every image anyway.
 */
function safeImageUrl(url: string): string | null {
  const trimmed = url.trim();
  if (/^(https?:|\/|\.{0,2}\/)/i.test(trimmed)) return trimmed;
  if (/^data:image\//i.test(trimmed)) return trimmed;
  return null;
}

/**
 * Slide pixels as a CSS inch measurement.
 *
 * Everything in the print sheet goes through this, so the PDF comes out at
 * exactly the size PowerPoint uses — 10in × 5.625in for 16:9, 10in × 7.5in for
 * 4:3. Dividing by CSS's own 96 px/in instead would give a 20-inch page that
 * every printer then scales or crops on its own terms.
 */
const inch = (px: number) => `${(px / PX_PER_INCH).toFixed(4)}in`;

/** One slide as HTML, for the print sheet. */
function slideHtml(slide: Slide, total: number): string {
  const parts = slide.blocks
    .map((block) => {
      switch (block.kind) {
        case "heading":
          return `<h${block.level === 1 ? 1 : 2}>${escapeHtml(plainText(block.text))}</h${block.level === 1 ? 1 : 2}>`;
        case "bullet":
          return `<p class="bullet" style="margin-left:${block.depth * 2}em">${escapeHtml(block.marker)}&nbsp;&nbsp;${escapeHtml(plainText(block.text))}</p>`;
        case "quote":
          return `<blockquote>&ldquo;${escapeHtml(plainText(block.text))}&rdquo;</blockquote>`;
        case "image": {
          const url = safeImageUrl(block.url);
          return url
            ? `<img src="${escapeHtml(url)}" alt="${escapeHtml(block.alt)}">`
            : `<p class="alt">${escapeHtml(plainText(block.alt)) || "(image)"}</p>`;
        }
        default:
          return `<p>${escapeHtml(plainText(block.text))}</p>`;
      }
    })
    .join("\n");

  const number = total > 1 ? `<span class="num">${slide.index + 1} / ${total}</span>` : "";
  return `<section class="slide layout-${slide.layout}">\n<div class="slide-inner">${parts}</div>\n${number}</section>`;
}

/**
 * Export as PDF, via the browser's own print dialogue.
 *
 * Same approach the manuscript export already uses, and it keeps the feature
 * dependency-free: `@page` at the chosen aspect with a hard break after each
 * slide gives one slide per page. Unlike the canvas path this one shows real
 * images, because the print window loads them the ordinary way.
 */
export async function exportSlidesPdf(
  slides: Slide[],
  title: string,
  style: SlideStyle = FALLBACK,
): Promise<void> {

  const { theme, aspect } = style;
  const { background, ink, soft, accent, ornament } = theme;
  const { margin, titleSize, titleOnlySize, bodySize, quoteSize } = SLIDE_GEOMETRY;
  const sans = SLIDE_FACES[theme.titleFace];
  const serif = SLIDE_FACES[theme.quoteFace];
  // The footer strip the accent rule and slide number sit in, kept out of the
  // content box so a full slide never runs into them. A theme with no ornament
  // has nothing down there and gets the space back.
  const footer = ornament ? SLIDE_GEOMETRY.footer : 0;

  await printHtml(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)} — slides</title>
<style>
  @page { size: ${inch(aspect.width)} ${inch(aspect.height)}; margin: 0; }
  /* Without this every browser helpfully drops the background when printing,
     which turns a dark theme into white-on-white. */
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; background: ${background}; color: ${ink}; font-family: ${sans}; }
  .slide {
    width: ${inch(aspect.width)};
    height: ${inch(aspect.height)};
    padding: ${inch(margin)} ${inch(margin)} ${inch(margin + footer)};
    display: flex;
    page-break-after: always;
    break-after: page;
    overflow: hidden;
    position: relative;
  }
  .slide:last-child { page-break-after: auto; break-after: auto; }
  .slide-inner { width: 100%; transform-origin: top left; }
  .layout-content .slide-inner { align-self: flex-start; }
  .layout-title .slide-inner,
  .layout-quote .slide-inner { margin: auto; text-align: center; }
${
    ornament
      ? `  .slide::after {
    content: "";
    position: absolute;
    left: ${inch(margin)};
    bottom: ${inch(margin - 29)};
    width: ${inch(120)};
    height: ${inch(9)};
    background: ${accent};
  }
  .num {
    position: absolute;
    right: ${inch(margin)};
    bottom: ${inch(margin - 40)};
    font-size: ${inch(32)};
    color: ${soft};
  }`
      : "  .num { display: none; }"
  }
  h1 { font-size: ${inch(titleSize)}; line-height: 1.2; margin: 0 0 0.45em; font-weight: 600; }
  .layout-title h1 { font-size: ${inch(titleOnlySize)}; margin: 0; }
  h2 { font-size: ${inch(titleSize * 0.7)}; line-height: 1.25; margin: 0 0 0.5em; font-weight: 600; }
  p { font-size: ${inch(bodySize)}; line-height: 1.35; margin: 0 0 0.32em; }
  p.bullet { text-indent: -1.6em; padding-left: 1.6em; }
  p.alt { color: ${soft}; font-size: ${inch(bodySize * 0.8)}; }
  /* Long words — a URL, an unhyphenated compound — break rather than run off
     the page, matching what the canvas renderer does. */
  h1, h2, p, blockquote { overflow-wrap: break-word; }
  blockquote {
    font-family: ${serif};
    font-style: italic;
    font-size: ${inch(quoteSize)};
    line-height: 1.35;
    margin: 0;
  }
  img { max-width: 100%; max-height: ${inch(aspect.height * 0.5)}; object-fit: contain; }
</style>
</head>
<body>
${slides.map((slide) => slideHtml(slide, ornament ? slides.length : 0)).join("\n")}
<script>
  // The canvas renderer shrinks an overfull slide until it fits; without the
  // same pass here the PDF would silently clip whatever ran past the bottom,
  // and one deck would come out two different ways in two formats. Same floor
  // as the canvas, so a slide that shrinks to 45% there shrinks to 45% here.
  function fit() {
    var slides = document.querySelectorAll('.slide');
    for (var i = 0; i < slides.length; i += 1) {
      var slide = slides[i];
      var inner = slide.querySelector('.slide-inner');
      if (!inner) continue;

      var box = getComputedStyle(slide);
      var room = slide.clientHeight
        - parseFloat(box.paddingTop)
        - parseFloat(box.paddingBottom);

      var scale = 1;
      while (inner.scrollHeight * scale > room && scale > ${MIN_SLIDE_SCALE}) scale -= 0.05;
      if (scale >= 1) continue;

      inner.style.transform = 'scale(' + scale + ')';
      if (slide.classList.contains('layout-content')) {
        // Widening before scaling means the text re-wraps at the full content
        // width in smaller type — what the canvas does — rather than simply
        // becoming a smaller picture of the same short lines. The origin stays
        // top-left, so the box still starts at the margin.
        inner.style.width = 100 / scale + '%';
      } else {
        // A title or quote card is centred by auto margins. Shrinking it about
        // its own middle leaves that centre where it was; touching the width
        // here would push it off to one side instead.
        inner.style.transformOrigin = 'center';
      }
    }
  }
  window.onload = function () { fit(); };
</script>
</body>
</html>`);
}
